import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import VideoCard from '@/components/feature/VideoCard';
import { useFavorites } from '@/hooks/useFavorites';
import { getVideoById, typeLabels, videos } from '@/mocks/videos';
import VideoPlayer from './components/VideoPlayer';

export default function DetailPage() {
  const { id } = useParams<{ id: string }>();
  const video = id ? getVideoById(id) : undefined;
  const { isFavorite, toggleFavorite } = useFavorites();
  const [toast, setToast] = useState('');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  useEffect(() => {
    if (video) {
      document.title = `${video.title} - 苹果视频`;
    }
  }, [video]);

  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(''), 2200);
    return () => clearTimeout(timer);
  }, [toast]);

  const related = useMemo(() => {
    if (!video) return [];
    return videos
      .filter((item) => item.id !== video.id && item.type === video.type)
      .map((item) => ({
        item,
        shared: item.tags.filter((tag) => video.tags.includes(tag)).length,
      }))
      .sort((a, b) => b.shared - a.shared || b.item.rating - a.item.rating)
      .slice(0, 4)
      .map((entry) => entry.item);
  }, [video]);

  if (!video) {
    return (
      <div className="flex min-h-screen flex-col bg-background-100">
        <Navbar />
        <main className="flex flex-1 items-center justify-center px-4 py-20">
          <div className="max-w-md rounded-lg border border-dashed border-background-300 bg-background-50 p-12 text-center">
            <i className="ri-video-off-line text-4xl text-foreground-300" aria-hidden="true" />
            <h1 className="mt-4 font-heading text-lg font-bold text-foreground-900">
              视频不存在或已下架
            </h1>
            <p className="mt-2 text-sm text-foreground-600">
              该视频可能已被移除，你可以返回首页继续浏览其他内容。
            </p>
            <Link
              to="/"
              className="mt-6 inline-flex items-center gap-2 rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const typeName = typeLabels[video.type];
  const categoryPath = `/category/${video.type}`;
  const favorited = isFavorite(video.id);

  const handleFavorite = () => {
    const nowFavorited = toggleFavorite(video.id);
    setToast(nowFavorited ? '已加入收藏' : '已取消收藏');
  };

  const handleShare = async () => {
    const link = window.location.href;
    try {
      await navigator.clipboard.writeText(link);
      setToast('链接已复制到剪贴板');
    } catch {
      setToast('复制失败，请手动复制地址栏链接');
    }
  };

  return (
    <div className="flex min-h-screen flex-col bg-background-100">
      <Navbar />

      <main className="flex-1">
        <div className="bg-background-50 px-4 pb-6 pt-6 md:px-8 md:pb-8 md:pt-8">
          <Breadcrumb
            items={[
              { label: '首页', to: '/' },
              { label: typeName, to: categoryPath },
              { label: video.title },
            ]}
          />
        </div>

        <div className="px-4 py-8 md:px-8 md:py-10">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
            <div className="lg:col-span-2">
              <h1 className="font-heading text-2xl font-black leading-tight text-foreground-950 md:text-4xl">
                {video.title}
              </h1>

              <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-foreground-500">
                <Link
                  to={categoryPath}
                  className="rounded bg-primary-50 px-2 py-0.5 font-medium text-primary-700 transition-colors hover:bg-primary-100"
                >
                  {typeName}
                </Link>
                <span className="flex items-center gap-1">
                  <i className="ri-calendar-line" aria-hidden="true" />
                  {video.uploadDate}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-time-line" aria-hidden="true" />
                  {video.duration}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-eye-line" aria-hidden="true" />
                  {video.views} 播放
                </span>
                <span className="flex items-center gap-1 text-accent-600">
                  <i className="ri-star-fill" aria-hidden="true" />
                  {video.rating.toFixed(1)}
                </span>
              </div>

              <div className="mt-6">
                <VideoPlayer video={video} />
              </div>

              <div className="mt-6 flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  onClick={handleFavorite}
                  className={`inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md px-5 py-2.5 text-sm font-medium transition-colors ${
                    favorited
                      ? 'bg-accent-100 text-accent-700'
                      : 'bg-primary-500 text-background-50 hover:bg-primary-600'
                  }`}
                >
                  <i
                    className={favorited ? 'ri-bookmark-fill text-lg' : 'ri-bookmark-line text-lg'}
                    aria-hidden="true"
                  />
                  {favorited ? '已收藏' : '收藏视频'}
                </button>
                <button
                  type="button"
                  onClick={handleShare}
                  className="inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-5 py-2.5 text-sm font-medium text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600"
                >
                  <i className="ri-share-forward-line text-lg" aria-hidden="true" />
                  复制链接
                </button>
              </div>

              <div className="mt-8">
                <h2 className="mb-3 font-heading text-lg font-bold text-foreground-950">
                  内容简介
                </h2>
                <p className="text-sm leading-loose text-foreground-700 md:text-base">
                  {video.synopsis}
                </p>
              </div>

              <div className="mt-6 flex flex-wrap items-center gap-2">
                {video.tags.map((tag) => (
                  <Link
                    key={tag}
                    to={`/search?q=${encodeURIComponent(tag)}`}
                    className="rounded-full bg-secondary-100 px-3 py-1 text-xs text-secondary-900 transition-colors hover:bg-secondary-200"
                  >
                    #{tag}
                  </Link>
                ))}
              </div>
            </div>

            <aside className="lg:col-span-1">
              <div className="rounded-lg border border-background-200/80 bg-background-50 p-5">
                <h2 className="mb-4 font-heading text-base font-bold text-foreground-950">
                  视频信息
                </h2>
                <dl className="space-y-3 text-sm">
                  <div className="flex items-center justify-between">
                    <dt className="text-foreground-500">分类</dt>
                    <dd className="font-medium text-foreground-800">{typeName}</dd>
                  </div>
                  <div className="flex items-center justify-between">
                    <dt className="text-foreground-500">地区</dt>
                    <dd className="font-medium text-foreground-800">{video.region}</dd>
                  </div>
                  <div className="flex items-center justify-between">
                    <dt className="text-foreground-500">年份</dt>
                    <dd className="font-medium text-foreground-800">{video.year}</dd>
                  </div>
                  <div className="flex items-center justify-between">
                    <dt className="text-foreground-500">时长</dt>
                    <dd className="font-medium text-foreground-800">{video.duration}</dd>
                  </div>
                  <div className="flex items-center justify-between">
                    <dt className="text-foreground-500">上传日期</dt>
                    <dd className="font-medium text-foreground-800">{video.uploadDate}</dd>
                  </div>
                </dl>
                <div className="mt-5 border-t border-background-200 pt-4">
                  <p className="mb-2 text-xs text-foreground-500">内容标签</p>
                  <div className="flex flex-wrap gap-2">
                    {video.genres.map((genre) => (
                      <span
                        key={genre}
                        className="rounded-md bg-accent-100 px-2 py-0.5 text-xs text-accent-800"
                      >
                        {genre}
                      </span>
                    ))}
                  </div>
                </div>
                <Link
                  to={categoryPath}
                  className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
                >
                  <i className="ri-arrow-left-line" aria-hidden="true" />
                  返回{typeName}
                </Link>
              </div>
            </aside>
          </div>

          {related.length > 0 && (
            <section className="mt-14" aria-label="相关推荐">
              <div className="mb-6 flex items-end justify-between">
                <h2 className="font-heading text-xl font-black text-foreground-950 md:text-2xl">
                  相关推荐
                </h2>
                <Link
                  to={categoryPath}
                  className="inline-flex items-center gap-1 text-sm font-medium text-primary-600 transition-colors hover:text-primary-700"
                >
                  更多{typeName}
                  <i className="ri-arrow-right-line" aria-hidden="true" />
                </Link>
              </div>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4 md:gap-6">
                {related.map((item) => (
                  <VideoCard key={item.id} video={item} />
                ))}
              </div>
            </section>
          )}
        </div>
      </main>

      <Footer />

      {toast && (
        <div className="fixed bottom-8 left-1/2 z-50 -translate-x-1/2 rounded-full bg-foreground-950/90 px-5 py-2.5 text-sm text-background-50 mv-animate-up">
          {toast}
        </div>
      )}
    </div>
  );
}